import React, { useState, useEffect } from "react";
import { Table, Spin, Select, DatePicker, Card } from "antd";
import {
  RiseOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { getData } from "../../config/api-connect";
import * as Styled from "./style.js";
import "./styles.css";
import {
  getSearchFilter,
  getDropdownFilter,
  getTimeSorter,
  getHoursSorter
} from "../../utils/filterHelper.jsx";
import Spinner from "../../components/spinner";
import moment from "moment";
import ReactHTMLTableToExcel from "react-html-table-to-excel";

const { RangePicker } = DatePicker;
const { Option } = Select;

const BuddyAttendance = () => {
  let local_city = JSON.parse(localStorage.getItem("city_Id"));
  let local_selectedVenueId = JSON.parse(
    localStorage.getItem("selectedOffice"),
  );
  if (!local_selectedVenueId) local_selectedVenueId = "All";

  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [summary, setSummary] = useState([]);
  const [venues, setVenues] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState(local_city);
  const [venuelist, setVenuelist] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState(local_selectedVenueId);
  const [dateRange, setDateRange] = useState([
    moment().subtract(6, "days"),
    moment(),
  ]);

  useEffect(() => {
    localStorage.setItem("selectedOffice", JSON.stringify(selectedVenueId));
    localStorage.setItem("city_Id", JSON.stringify(cityId));
    if (cityId !== "") getBuddyAttendance();
  }, [selectedVenueId, cityId]);

  useEffect(() => {
    getVenues();
  }, []);

  useEffect(() => {
    if (cities.length) getVenuesInCity();
  }, [cities, cityId]);

  const handleCityChange = (value) => {
    if (value.length > 0) {
      const last_selected = value[value.length - 1];
      if (last_selected === "All") setCityId(["All"]);
      else setCityId(value.filter((v) => v !== "All"));
    } else setCityId(["All"]);
  };

  const getVenuesInCity = () => {
    if (cities[0] && !cityId) setCityId([cities[0].id]);

    let filteredVenues;
    if (cityId === "All" || (Array.isArray(cityId) && cityId.includes("All"))) {
      filteredVenues = venues;
    } else {
      filteredVenues = Array.isArray(cityId)
        ? venues.filter((item) => cityId.includes(item.city))
        : venues;
    }

    setVenuelist(filteredVenues);
    const venueExists = filteredVenues.some(
      (item) => item.id === selectedVenueId,
    );
    if (!venueExists) setSelectedVenueId("All");
  };

  const getVenues = async () => {
    try {
      const response = await getData(
        `/business/get_venues_for_buddies/?office_only=true`,
      );
      setVenues(response.result);
      setCities(response.cities);
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    getBuddyAttendance();
  }, [dateRange]);

  const getBuddyAttendance = async () => {
    setLoading(true);
    try {
      const [startMoment, endMoment] = dateRange;
      const start_date = startMoment.format("YYYY-MM-DD");
      const end_date = endMoment.format("YYYY-MM-DD");

      const response = await getData(
        `/buddy/get_buddy_attendance_data/?start_date=${start_date}&end_date=${end_date}&venue_id=${selectedVenueId}&city_id=${cityId}`,
      );
      setAttendanceData(response.data || []);
      setSummary(response.summary || []);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderHeader = (title) => (
    <div
      style={{ textAlign: "center", fontWeight: "bold", textWrap: "nowrap" }}
    >
      {title}
    </div>
  );

  const getDateSorter = (dataIndex) => ({
    sorter: (a, b) =>
      moment(a[dataIndex]).valueOf() - moment(b[dataIndex]).valueOf(),
  });

  const getColor = (status) => {
    let color = "#22c55e";
    if (status && status.toLowerCase() == "absent") {
      color = "#ef4444";
    } else if (status && status.toLowerCase() == "late") {
      color = "#f59e42";
    }
    return color;
  };

  const columns = [
    {
      title: renderHeader("Date"),
      dataIndex: "date",
      align: "center",
      ...getDateSorter("date"),
    },
    {
      title: renderHeader("Employee"),
      dataIndex: "employee",
      align: "center",
      ...getSearchFilter("employee", "Employee"),
    },
    {
      title: renderHeader("Department"),
      dataIndex: "department",
      align: "center",
      ...getDropdownFilter("department", attendanceData),
    },
    {
      title: renderHeader("Check-In"),
      dataIndex: "check_in",
      align: "center",
      ...getTimeSorter("check_in"),
    },
    {
      title: renderHeader("Check-Out"),
      dataIndex: "check_out",
      align: "center",
      ...getTimeSorter("check_out"),
    },
    {
      title: renderHeader("Hours Worked"),
      dataIndex: "hours_worked",
      align: "center",
      ...getHoursSorter("hours_worked"),
    },
    {
      title: renderHeader("Status"),
      dataIndex: "status",
      align: "center",
      render: (value) => (
        <span
          style={{
            background: getColor(value),
            padding: "4px 10px",
            borderRadius: 15,
            color: "#fff",
          }}
        >
          {value}
        </span>
      ),
      ...getDropdownFilter("status", attendanceData),
    },
  ];

  return (
    <Spin spinning={loading} indicator={<Spinner />}>
      <Styled.MainContainer className="buddyAttendance">
        <Styled.TopDiv>
          <Styled.SelectorContainer>
            {cities.length > 0 && (
              <Styled.Selector>
                <span className="indicatorText">City</span>
                <Select
                  mode="multiple"
                  className="mainSelector"
                  style={{ width: 150 }}
                  showSearch
                  placeholder="Search to Select"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.name.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleCityChange}
                  value={cityId}
                  bordered={false}
                >
                  <Option value="All" name="All">
                    All
                  </Option>
                  {cities.map((item) => (
                    <Option value={item.id} key={item.id} name={item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Styled.Selector>
            )}

            {venuelist && venuelist.length > 0 && (
              <Styled.Selector>
                <span className="indicatorText">Office</span>
                <Select
                  className="mainSelector"
                  style={{ width: 300 }}
                  showSearch
                  placeholder="Search to Select"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.name.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={setSelectedVenueId}
                  value={selectedVenueId}
                  bordered={false}
                >
                  <Option value="All" name="All">
                    All
                  </Option>
                  {venuelist.map((item) => (
                    <Option value={item.id} key={item.id} name={item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Styled.Selector>
            )}
          </Styled.SelectorContainer>
          <Styled.Selector>
            <span className="indicatorText">Date Range</span>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [])}
              format="DD-MM-YYYY"
              allowClear={false}
            />
          </Styled.Selector>
        </Styled.TopDiv>
        <span></span>
        <Styled.NewHeader>
          <span></span>
          <span> Attendance Dashboard </span>
          {
            <>
              <ReactHTMLTableToExcel
                className="btn btn-sm downloadBtn"
                table="table-for-download"
                filename={`Attendance-${dateRange[0].format("MMM-YYYY")}-${dateRange[1].format("MMM-YYYY")}`}
                sheet={`Buddy Attendance`}
                buttonText="Download"
              />
            </>
          }
        </Styled.NewHeader>

        <div style={{ margin: "10px 0", display: "flex", gap: "10px" }}>
          <Card
            // size="small"
            title="Attendance Rate"
            extra={<RiseOutlined style={{ color: "#6a7282" }} />}
            style={{
              // width: 300,
              borderRadius: 15,
              flex: 1,
            }}
            headStyle={{ borderBottom: "none", fontWeight: 600 }}
          >
            <h3 style={{ margin: 0, fontWeight: 600 }}>
              {summary && summary.attendance_rate
                ? `${summary.attendance_rate}%`
                : "0%"}
            </h3>
            <p style={{ margin: 0, color: "#6a7282" }}>Present vs Total</p>
          </Card>
          <Card
            // size="small"
            title="Late Arrivals"
            extra={<ClockCircleOutlined style={{ color: "#6a7282" }} />}
            style={{
              // width: 300,
              borderRadius: 15,
              flex: 1,
            }}
            headStyle={{ borderBottom: "none", fontWeight: 600 }}
          >
            <h3 style={{ margin: 0, fontWeight: 600 }}>
              {summary && summary.late_arrivals ? summary.late_arrivals : 0}
            </h3>
            <p style={{ margin: 0, color: "#6a7282" }}>After Check-In Time</p>
          </Card>
          <Card
            // size="small"
            title="Avg. Hours/Day"
            extra={<CalendarOutlined style={{ color: "#6a7282" }} />}
            style={{
              // width: 300,
              borderRadius: 15,
              flex: 1,
            }}
            headStyle={{ borderBottom: "none", fontWeight: 600 }}
          >
            <h3 style={{ margin: 0, fontWeight: 600 }}>
              {summary && summary.avg_hours ? summary.avg_hours : 0}
            </h3>
            <p style={{ margin: 0, color: "#6a7282" }}>Work hours</p>
          </Card>
        </div>

        <div className="recordCount">{attendanceData.length || 0} records</div>
        <Table
          className="drill-down-table table_container"
          rowKey="id"
          columns={columns}
          dataSource={attendanceData}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ x: "max-content" }}
        />
        <table id="table-for-download" style={{ display: "none" }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Employee</th>
              <th>Department</th>
              <th>Check-In</th>
              <th>Check-Out</th>
              <th>Hours Worked</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {attendanceData.map((row, index) => (
              <tr key={index}>
                <td>{row.date}</td>
                <td>{row.employee}</td>
                <td>{row.department}</td>
                <td>{row.check_in}</td>
                <td>{row.check_out}</td>
                <td>{row.hours_worked}</td>
                <td>{row.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Styled.MainContainer>
    </Spin>
  );
};

export default BuddyAttendance;
